import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Launch as LaunchIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ShoppingCart as OrderIcon,
  Autorenew as SubscriptionIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import type { Patient } from '../../services/patientService';
import { GroupPatientChip } from '../common/GroupPatientChip';
import { PatientKrollStatusChips } from '../common/PatientKrollStatusChips';
import {
  searchAll,
  GlobalSearchOrder,
  GlobalSearchSubscription,
} from '../../services/globalSearchService';

interface PatientQuickViewPanelProps {
  patient: Patient;
  pharmacyId: string | null;
  onOpenPatient360: () => void;
}

const stageLabels: Record<string, string> = {
  billing: 'Billing',
  clinical_verification: 'Clinical',
  packaging: 'Packaging',
  product_verification: 'Product Verify',
  ready_to_ship: 'Ready to Ship',
  staging: 'Staging',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  ready_for_pickup: 'Ready for Pickup',
  picked_up: 'Picked Up',
  delivery_missed: 'Missed',
};

function formatDDMMYYYY(dateStr?: string | null): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

/**
 * GP-308 — Quick-view side panel.
 *
 * Renders identity (name, DOB, address, phone, email) from the already-loaded
 * Kroll Patient object, plus a lookup against the existing pharmacy-scoped
 * `GET /api/search?q=<patientName>` for active orders + active subscriptions.
 * Reusing the global-search endpoint avoids a dedicated quick-view backend.
 *
 * Filters the search response to the SAME pharmacy the patient was found in
 * (search response is already pharmacy-scoped via the user's selected pharmacy
 * — same scope as the Kroll search that returned this patient).
 */
export function PatientQuickViewPanel({
  patient,
  pharmacyId,
  onOpenPatient360,
}: PatientQuickViewPanelProps) {
  const [orders, setOrders] = useState<GlobalSearchOrder[]>([]);
  const [subscriptions, setSubscriptions] = useState<GlobalSearchSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch active orders + subscriptions for this patient. Re-runs whenever a
  // different patient is selected.
  useEffect(() => {
    if (!patient.lastName) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // Query by `Lastname, Firstname` — matches the patientName format stored
    // on orders/billing_items rows. The global search's tokenized matcher
    // handles partials AND-style, so this also catches name variants.
    const query = `${patient.lastName}, ${patient.firstName}`.trim();
    searchAll(query, 25, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setOrders(data.orders ?? []);
        setSubscriptions(data.subscriptions ?? []);
        setLoading(false);
      })
      .catch((err: any) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setError('Could not load orders and subscriptions.');
        setLoading(false);
      });
    return () => controller.abort();
  }, [patient.id, patient.lastName, patient.firstName]);

  const addressLine = [
    patient.addresses?.[0]?.street1,
    patient.addresses?.[0]?.street2,
    patient.addresses?.[0]?.city,
    patient.addresses?.[0]?.state,
    patient.addresses?.[0]?.zipCode,
  ]
    .filter(Boolean)
    .join(', ');
  const phone = patient.primaryPhone || patient.phone || patient.mobilePhone;
  const email = patient.email;

  return (
    <Box data-testid="patient-quick-view" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Identity */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
              {patient.lastName}, {patient.firstName}
            </Typography>
            {/* GP-1402: the same Kroll annotations the result row carries. A closed
                or deceased file must be just as obvious once the row is opened —
                this panel is where staff decide whether to act on the patient. */}
            <PatientKrollStatusChips patient={patient} inline={false} />
          </Box>
          <Button
            variant="contained"
            size="small"
            endIcon={<LaunchIcon />}
            onClick={onOpenPatient360}
            disabled={!pharmacyId}
          >
            Open Patient 360
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.secondary', flexWrap: 'wrap' }}>
          {patient.dateOfBirth && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                DOB {formatDDMMYYYY(patient.dateOfBirth)}
              </Typography>
            </Box>
          )}
          {patient.mrn && (
            <Typography variant="caption">MRN {patient.mrn}</Typography>
          )}
          {patient.healthCardNumber && (
            <Typography variant="caption">HC {patient.healthCardNumber}</Typography>
          )}
          {/* GP-1348: which nursing/group home this patient belongs to, by name —
              the disambiguator DOB alone doesn't give when several patients share
              a name. Renders nothing for a patient with no home. */}
          <GroupPatientChip group={patient.krollGroup} maxWidth={200} />
        </Box>
      </Box>

      <Divider />

      {/* Contact + address */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {addressLine && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <HomeIcon sx={{ fontSize: 16, mt: 0.25, color: 'text.secondary' }} />
            <Typography variant="body2">{addressLine}</Typography>
          </Box>
        )}
        {phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{phone}</Typography>
          </Box>
        )}
        {email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{email}</Typography>
          </Box>
        )}
      </Box>

      <Divider />

      {error && <Alert severity="warning" variant="outlined">{error}</Alert>}

      {/* Active orders */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <OrderIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'text.secondary',
            }}
          >
            Active orders {!loading && `(${orders.length})`}
          </Typography>
        </Box>
        {loading && <Skeleton height={42} sx={{ borderRadius: 1 }} />}
        {!loading && orders.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No active orders for this patient.
          </Typography>
        )}
        {!loading && orders.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {orders.map((o) => (
              <Box
                key={o.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 170 }} noWrap>
                  {o.orderNumber}
                </Typography>
                {o.rxNumber && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Rx {o.rxNumber}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={stageLabels[o.workflowStage] || o.workflowStage}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
                {o.deliveryDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatDDMMYYYY(o.deliveryDate)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Subscriptions */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <SubscriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: 'text.secondary',
            }}
          >
            Current subscriptions {!loading && `(${subscriptions.length})`}
          </Typography>
        </Box>
        {loading && <Skeleton height={42} sx={{ borderRadius: 1 }} />}
        {!loading && subscriptions.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No active subscriptions for this patient.
          </Typography>
        )}
        {!loading && subscriptions.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {subscriptions.map((s) => (
              <Box
                key={s.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  bgcolor: 'action.hover',
                }}
              >
                {s.rxNumber && (
                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 110 }} noWrap>
                    Rx {s.rxNumber}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {s.medicationName}
                </Typography>
                <Chip
                  label={s.subscriptionStatus || 'active'}
                  size="small"
                  variant="outlined"
                  color={s.subscriptionStatus === 'active' ? 'success' : 'default'}
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
                {s.dueDate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 88, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  >
                    Due {formatDDMMYYYY(s.dueDate)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
